/**
 * `<ProjectActivityFeed/>` — the project-wide recent-activity feed for the Home
 * dashboard (#1043, founder prod-inspection round-3 item 2, 2026-05-27).
 *
 * Round-3 relocated History off the Taskmap window (the docked PR-C rail was
 * removed) and onto the Home dashboard, where it sits to the RIGHT of the
 * filterable task list. Unlike the old rail, the dashboard has no canvas
 * selection — so this feed is NOT node-selection-driven. It always shows the
 * WHOLE project's recent activity, newest-first, exactly the project-wide
 * default the retired History tab + rail both opened on.
 *
 * Round-4 ⑤.c (2026-05-28, QA-B finding B1): the feed reads the WHOLE forest,
 * not just the first root's subtree. Previously, in a multi-outcome project,
 * ~80% of events went missing because only `roots[0]`'s subtree was fetched.
 * The feed now fans out across EVERY outcome root via `useForestHistory`.
 *
 * **Reuse, never rebuild (`feedback_taskmap_clean_code`).** Nothing about the
 * timeline is re-implemented here. The feed COMPOSES the shipped primitives the
 * drawer + the retired tab/rail all used:
 *   - `useForestHistory(teamId, rootIds, "subtree")` — fans out one subtree
 *     fetch per root, merges + dedups + sorts newest-first; cache keys mirror
 *     `useNodeHistory` so per-root callers dedup through the same QueryClient;
 *   - `groupEventsByDay` — for the day/event counts in the header;
 *   - `<TaskTimeline/>` — the day-grouped, newest-first rail engine.
 * The feed's only own logic is the panel chrome (compact header + footer).
 *
 * **Brand firewall (§A9 — sharpest on a history surface).** Provenance, never a
 * scorecard: every line is about the WORK, counts are of events/days, blocked
 * reads calm amber (inherited from `theme.stateStyle`), and the footer states
 * the rule plainly. No per-person aggregation, ranking, or tier language — the
 * timeline engine enforces this; this feed never adds any.
 *
 * Degradation (the shipped idiom): loading → "Loading activity…"; error /
 * undeployed-endpoint 404 → "History isn’t available here yet."; empty → "No
 * activity yet." — never a crash.
 *
 * Isolation (`taskmap ⊥ scheduler`): taskmap-local hooks + components + theme
 * only — no scheduler import.
 */

import { useMemo } from "react";

import { useForestHistory } from "../api/useForestHistory";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { LONG_HISTORY_THRESHOLD, TaskTimeline } from "./TaskTimeline";
import { groupEventsByDay } from "./groupEventsByDay";

export interface ProjectActivityFeedProps {
  readonly teamId: string | undefined;
  /** Team roster, to resolve actor + referenced-member names in the timeline. */
  readonly members: TaskMember[];
  /** Team IANA zone for day bucketing + timestamps. */
  readonly teamTimezone?: string | undefined;
  /**
   * Every outcome root in the project — the feed fans out one subtree-history
   * fetch per id and merges them newest-first. An empty array (no outcomes
   * yet) → the calm empty state, no fetches.
   */
  readonly rootIds: readonly string[];
  /** node id → title, to name referenced nodes + tag subtree events. */
  readonly nodeTitleById: Map<string, string>;
}

export function ProjectActivityFeed({
  teamId,
  members,
  teamTimezone,
  rootIds,
  nodeTitleById,
}: ProjectActivityFeedProps) {
  const theme = useMapTheme();
  const c = theme.palette;

  // Forest fan-out: one subtree fetch per outcome root, merged + sorted
  // newest-first. No roots → no fetches, stable empty result.
  const { events, isLoading, isError } = useForestHistory(teamId, rootIds, "subtree");
  const hasRoots = rootIds.length > 0;

  const groups = useMemo(
    () => groupEventsByDay(events, { tz: teamTimezone }),
    [events, teamTimezone],
  );

  return (
    <aside
      data-testid="project-activity-feed"
      aria-label="Project activity"
      // `md:flex-1` lets the feed FILL its bounded dashboard column on desktop
      // (round-4 item ①, #1043) so the scrolling body (`overflow-auto` below)
      // is capped to the column's height instead of growing the page. The
      // `min-h-0` (kept on all breakpoints) is the standard flex-shrink-below-
      // content escape. Outside a bounded flex parent (e.g. the phone-stacked
      // dashboard) the flex-grow is inert and the feed flows at its natural
      // height.
      className="flex min-h-0 flex-col rounded-md border md:flex-1"
      style={{ borderColor: c.rule, backgroundColor: c.canvas, fontFamily: theme.fonts.sans }}
    >
      {/* Compact header — a mono "ACTIVITY" kicker + serif subject + the
          event/day counts. */}
      <header className="border-b px-4 py-3" style={{ borderColor: c.rule }}>
        <div
          data-testid="project-activity-kicker"
          className="text-[10px] uppercase"
          style={{ fontFamily: theme.fonts.mono, letterSpacing: "0.1em", color: c.muted }}
        >
          Activity
        </div>
        <h2
          data-testid="project-activity-heading"
          className="mt-0.5 truncate text-[18px] italic leading-tight"
          style={{ fontFamily: theme.fonts.serif, fontWeight: 400, color: c.ink }}
        >
          Recent across the project
        </h2>
        {!isLoading && !isError && hasRoots && (
          <div
            data-testid="project-activity-counts"
            className="mt-0.5 text-[10.5px]"
            style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.02em" }}
          >
            {events.length} event{events.length === 1 ? "" : "s"} · {groups.length} day
            {groups.length === 1 ? "" : "s"}
          </div>
        )}
      </header>

      {/* Body — the shipped timeline engine, reused verbatim. */}
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        {!hasRoots ? (
          <FeedMessage testId="project-activity-empty" title="No activity yet.">
            As the team works, the project’s story shows up here.
          </FeedMessage>
        ) : isLoading ? (
          <FeedMessage testId="project-activity-loading" title="Loading activity…">
            A calm record of what changed and when.
          </FeedMessage>
        ) : isError ? (
          <FeedMessage
            testId="project-activity-unavailable"
            title="History isn’t available here yet."
          >
            The story of this work will appear here once it’s switched on.
          </FeedMessage>
        ) : events.length === 0 ? (
          <FeedMessage testId="project-activity-empty" title="No activity yet.">
            As the team works, the project’s story shows up here.
          </FeedMessage>
        ) : (
          <TaskTimeline
            events={events}
            members={members}
            nodeTitleById={nodeTitleById}
            // Across the forest, every outcome root is a focal node — own
            // events on ANY root render untagged, every descendant event gets
            // an "on «task»" tag. The shipped timeline only accepts one
            // focalNodeId, so we pass the first root; the others are still
            // suppressed at the source by `useForestHistory` returning their
            // own events alongside descendants, but they'll render tagged in
            // multi-root projects. (Acceptable: the tag names the outcome,
            // which is useful context across the forest.)
            // TS-narrowing: this branch is only reachable when `hasRoots`
            // (so `rootIds[0]` is defined); fall back is a defensive no-op.
            focalNodeId={rootIds[0] ?? ""}
            showNodeAttribution
            compact
            collapsibleDays={events.length > LONG_HISTORY_THRESHOLD}
            tz={teamTimezone}
          />
        )}
      </div>

      {/* The B3.1 brand-firewall footer, verbatim. */}
      <div
        data-testid="project-activity-footer"
        className="border-t px-4 py-3 text-[10.5px] italic"
        style={{
          borderColor: c.rule,
          color: c.faint,
          fontFamily: theme.fonts.mono,
          letterSpacing: "0.02em",
        }}
      >
        Reads as a story of the work — never a tally of a person.
      </div>
    </aside>
  );
}

// ── Calm message states (loading / error / empty) ────────────────────────────

function FeedMessage({
  testId,
  title,
  children,
}: {
  testId: string;
  title: string;
  children: React.ReactNode;
}) {
  const theme = useMapTheme();
  const c = theme.palette;
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-2 py-12 text-center"
    >
      <span className="text-2xl" aria-hidden>
        🕑
      </span>
      <div className="text-[13px] font-semibold" style={{ color: c.slate }}>
        {title}
      </div>
      <div className="max-w-[16rem] text-[12px]" style={{ color: c.faint }}>
        {children}
      </div>
    </div>
  );
}
